/* tslint:disable:import-spacing no-inferrable-types variable-name align no-trailing-whitespace */

import { first }           from "rxjs/operators";

import { Train }           from '@/models/scenario/train';
import { Logger }          from '@/util/logger';
import { ViewerInterface } from './viewer.interface';
import { Scenario }        from '@/models/scenario/scenario';
import { InterLink }       from '@/models/scenario/inter.link';
import { Track }           from '@/models/scenario/track';
import { ViewMemento }     from '@/screens/ide/stringviewer/view.memento';
import { UserLogService }  from "@/services/user.log.service";
import { TrainDirection }  from "@/models/scenario/train.class";
import { MeshLineBuilder } from "@/helpers/lines/mesh.line.builder";

import * as THREE          from 'three';
import {
  Vector2,
  Box2,
  Mesh,
  BufferGeometry,
  MeshBasicMaterial,
  Color,
  DoubleSide,
  Group,
  LineBasicMaterial,
  BufferAttribute,
  LineSegments
}                          from 'three';

/** go / no go parking bucket in each interlink */
export interface InterLinkAuth {

  /** to interlink */
  link: InterLink;

  /** eastbound */
  east: {train: Train, track: Track, reason: string}[];

  /** westbound */
  west: {train: Train, track: Track, reason: string}[];

  /** the top (higher) distance */
  top: number;

  /** the bottom (lower) distance */
  bottom: number;
}

/**
 *
 * @class AuthorityManager
 *
 * @brief helper to draw the "extra" info for parkability after the limit line cursor (if enabled)
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      November 2019
 *
 */
export class AuthorityManager {

  /** @brief _viewerViewerInterface the main viewer of the graph */
  private _viewer: ViewerInterface = null;

  /** the viewer windows have to have unique DOM ID since there may be more than one viewer in the browser */
  private    _uuid: number          = 0;

  /** the data model for the physical network and trains */
  private _scenario: Scenario       = null;

  /** the lookup table for parking, ordered highest mileage first [0] */
  private _slots: InterLinkAuth[]   = [];

  /** the most westward mileage (top of graph) */
  private _maxMile: number          = 0;

  /** the interlinks where we can park */
  private _links: InterLink[]       = [];

  /** the trains that would want to park */
  private _trains: Train[]          = [];
  private _trainById: Map<number, Train> = new Map<number, Train>();

  /** the color for the eastbound blocks */
  private _eastColor: string     = 'yellow';

  /** the color for the westbound blocks */
  private _westColor: string      = 'blue';

  /** the thickness of the extra info geometry */
  private _thickness: number        = 50;

  /** the world time axis (x) position */
  private _wt: number               = 0.0;

  /** indicator for having a hover (or not) */
  private _hover: {slot: InterLinkAuth, east: boolean} = null;

  /** the cached results from the server query on the most recent time point */
  private _authorities: any[]       = [];

  /** @brief standard constructor */
  constructor(viewer: ViewerInterface, scenario: Scenario, uuid: number, private userlog: UserLogService) {

    this._viewer   = viewer;
    this._uuid     = uuid;
    this._scenario = scenario;

    this.setScenario(this._scenario);
  }

  /** update the data model */
  setScenario(scenario: Scenario) {

    this._scenario = scenario;
    this._links    = [];
    this._maxMile  = 0;

    if (this._scenario == null) {

      return ;
    }

    this._links    = this._scenario.interlinks();

    /* find the max mileage; interlinks are ordered so this should just be the first interlink mileage */

    this._maxMile = Math.max(this._links[0]._before._mileage, this._links[0]._after ? this._links[0]._after._mileage: 0);
  }

  /** clear all train parking status */
  clear() {

    this._slots.forEach((item: InterLinkAuth) => {

      item.east       = [];
      item.west       = [];

    });

    this._authorities = [];
    this._trains      = [];

    this._trainById.clear();
  }

  /** test all the parking slots */
  setTrains(trains: Train[]) {

    const pm: AuthorityManager = this;

    pm.clear();

    pm._trains = trains;
    pm._slots  = [];

    /* for each train, walk the interlinks and figure out which tracks the train can or can't use */

    pm._links.forEach((bundle: InterLink, i: number) => {

      if ((bundle._before == null)||(bundle._after == null)) {

        /* has to have a decision point on both sides */

        return ;
      }

      const nextBundle: InterLink = ((i + 1) < pm._links.length) ? pm._links[i + 1] : null;

      const slot: InterLinkAuth = {
        link:    bundle,
        east:    [],
        west:    [],
        top:     bundle.maxMiles(),
        bottom:  (nextBundle != null) ? nextBundle.maxMiles() : 0
      };

      /* save this slots */

      pm._slots.push(slot);
    });

    this._trains.forEach((train: Train) => {

      this._trainById.set(train._generatedId, train);

    });
  }

  /** test if we currently have a hover indicator */
  hasHover(): boolean {

    return this._hover != null;
  }

  /** fetch the hover if there is one */
  hover(): {slot: InterLinkAuth, east: boolean} {

    return this._hover;
  }

  /** indicate the user is hovering over this allowed or denied block */
  addHover(slot: InterLinkAuth, east: boolean) {

    this._hover = {slot, east};
  }

  /** remove any hover position */
  clearHover() {

    this._hover = null;
  }

  /** if there is a park bounding box that contains this point, return it. */
  contains(x: number, y: number): {slot: InterLinkAuth, east: boolean} {

    const pm: AuthorityManager = this;

    /* before looking for a hit, determine left / right bounds for the column of boxes */

    const vm: ViewMemento = this._viewer.viewMemento();
    const tx1: number     = pm._viewer.screenToWorld( 0     , 0)[0];
    const tx2: number     = pm._viewer.screenToWorld( this._thickness, 0)[0];

    const width: number   = Math.abs(tx2 - tx1);

    const left: number    = this._wt;
    const right: number   = this._wt + (width / vm._timeScale);
    const middle: number  = left + ((right - left) / 2.0);

    const wx: number = x / vm._timeScale;
    const wy: number = y / vm._distanceScale;

    /* can we exit early? */

    if ((wx < left) || (wx > right)) {

      /* no match in entire column */

      return null;
    }

    if ((wy > this._slots[0].top) || (wy < 0)) {

      /* vertically off-screen */

      return null;
    }

    /* walk the slots looking for one that contains point */

    let slot: InterLinkAuth = null;
    let east: boolean       = true;

    this._slots.forEach((item: InterLinkAuth ) => {

      if (slot != null) {

        return ;
      }

      if ((item.top >= wy) && (item.bottom <= wy)) {

        slot = item;
        east = (wx <= middle);
      }

    });

    /* pass back any hit */

    return (slot == null) ? null : {slot, east: !east};
  }

  /** we already have results, just refresh the display (i.e. scale changed) */
  redraw(object: Group, hoverObject: Group): void {

    const am: AuthorityManager = this;

    if (!am._scenario.hasUserLogging()) {

      /* user logging isn't even enabled! */

      return ;
    }

    object.remove(...object.children);
    hoverObject.remove(...object.children);

    /* figure out how wide the line should be in world space, to always be a fixed thickness on screen */

    const tx1: number     = am._viewer.screenToWorld(0, 0)[0];
    const tx2: number     = am._viewer.screenToWorld(this._thickness, 0)[0];
    let thickness: number = Math.abs(tx2 - tx1);
    let results: Mesh[]   = [];

    const vm: ViewMemento = am._viewer.viewMemento();

    /*
     * we draw at the limit line, and nudge it over so that our poly lines are on
     * the right side of the limit line.
     *
     */

    am._slots.forEach((link: InterLinkAuth) => {

      [link.west, link.east].forEach((items: { train: Train, track: Track, reason: string }[], j: number) => {

        if (items.length == 0) {

          /* empty slot in this direction */

          return;
        }

        let color: string    = (j == 0) ? am._westColor : am._eastColor;
        const offset: number = (j == 0) ? 0 : thickness / 2;

        const sx: number     = am._wt * vm._timeScale + offset + thickness / 4;
        const sy: number     = link.top * vm._distanceScale;
        const ex: number     = am._wt * vm._timeScale + offset + thickness / 4;
        const ey: number     = link.bottom * vm._distanceScale;

        /* generate the polyline  */

        let lineMesh: Mesh = (new MeshLineBuilder())
          .setColor( new Color(color))
          .setThickness(am._thickness)
          .setOpacity(1.0)
          .buildFromLines([[sx, sy, ex, ey]], am._viewer.width(), am._viewer.height());

        /* save the mesh for this box */

        results.push(lineMesh);

      });

    });

    /* all done */

    if (results.length > 0) {

      object.add(...results);
    }

    /* add some grid lines to help tell where blocks should or should not be */

    let gridGeo: BufferGeometry  = new BufferGeometry();
    let positions: Float32Array  = new Float32Array((am._slots.length + 1) * 6);
    let colors: Float32Array     = new Float32Array((am._slots.length + 1) * 6);
    let indices: number[]        = [];
    let vIndex: number           = 0;
    let cIndex: number           = 0;
    let pIndex: number           = 0;
    let color: Color             = new Color('grey');

    gridGeo.name                 = 'authority-grid';

    am._slots.forEach((link: InterLinkAuth, j: number) => {

      const sx: number = am._wt      * vm._timeScale;
      const sy: number = link.top    * vm._distanceScale;
      const ex: number = am._wt    * vm._timeScale + thickness;
      const ey: number = link.bottom * vm._distanceScale;

      /* push this segment */

      positions[vIndex] = sx;
      positions[vIndex + 1] = sy;
      positions[vIndex + 2] = 0;

      positions[vIndex + 3] = ex;
      positions[vIndex + 4] = sy;
      positions[vIndex + 5] = 0;

      indices.push(pIndex);
      indices.push(pIndex + 1);

      vIndex += 6;
      pIndex += 2;

      /* push the color for this segment */

      colors[cIndex] = color.r;
      colors[cIndex + 1] = color.g;
      colors[cIndex + 2] = color.b;

      colors[cIndex + 3] = color.r;
      colors[cIndex + 4] = color.g;
      colors[cIndex + 5] = color.b;

      cIndex += 6;

      if (j == (this._slots.length - 1)) {

        /* at the bottom draw the lower one as well */

        positions[vIndex] = sx;
        positions[vIndex + 1] = ey;
        positions[vIndex + 2] = 0;

        positions[vIndex + 3] = ex;
        positions[vIndex + 4] = ey;
        positions[vIndex + 5] = 0;

        indices.push(pIndex);
        indices.push(pIndex + 1);

        vIndex += 6;
        pIndex += 2;

        /* push the color for this segment */

        colors[cIndex] = color.r;
        colors[cIndex + 1] = color.g;
        colors[cIndex + 2] = color.b;

        colors[cIndex + 3] = color.r;
        colors[cIndex + 4] = color.g;
        colors[cIndex + 5] = color.b;

        cIndex += 6;

      }

    });

    /* create the buffered geometry for "one big line" that represents all the train movement lines */

    let gridMaterial: LineBasicMaterial = new LineBasicMaterial({
      color: color,
      vertexColors: true
    });

    gridGeo.setAttribute('position', new BufferAttribute(positions, 3));
    gridGeo.setIndex(new BufferAttribute(new Uint32Array(indices), 1));

    gridGeo.setAttribute('color', new BufferAttribute(colors, 3));

    const segments: LineSegments = new LineSegments(gridGeo, gridMaterial);

    object.add(segments);

    /* finally the hover highlight, if there is one */

    hoverObject.remove(...hoverObject.children);

    if (!this.hasHover()) {

      return ;
    }

    const hovC: Color    = new Color('white');

    const offset: number = am._hover.east ?  thickness / 2 : 0;
    const sx: number     = am._wt * vm._timeScale + offset + thickness / 4;
    const sy: number     = am._hover.slot.top * vm._distanceScale;
    const ex: number     = am._wt * vm._timeScale + offset + thickness / 4;
    const ey: number     = am._hover.slot.bottom * vm._distanceScale;

    /* generate the hover polyline  */

    let lineMesh: Mesh = (new MeshLineBuilder())
      .setColor( new Color(color))
      .setThickness(am._thickness)
      .setOpacity(1.0)
      .buildFromLines([[sx, sy, ex, ey]], am._viewer.width(), am._viewer.height());

    lineMesh.geometry.name = `authority-hover-${sy}`;

    hoverObject.add(lineMesh);
  }

  /** generate the "boxes" that make up the parkability hover zones */
  generate(wt: number, thickness: number, object: Group, hoverObject: Group): void {

    const am: AuthorityManager = this;

    if (!am._scenario.hasUserLogging()) {

      /* user logging isn't even enabled! */

      return ;
    }

    const context: string      = am._scenario.userLog();
    const trainNames: string[] = am._trains.map(x => Train.iid(x));
    const oldWt: number        = am._wt;
    am._thickness              = thickness;
    am._wt                     = wt;

    if (oldWt != am._wt) {

      am._slots.forEach((item: InterLinkAuth) => {

        item.east       = [];
        item.west       = [];

      });

      /* the time position has changed, need new data before we can draw */

      am.userlog.authorities(context, wt * 3600.0, trainNames).pipe(first())
        .subscribe(
          (children: any) => {

            am._authorities = children;

            /* put the trains into their slots */

            am._authorities.forEach((child: any, i: number) => {

              /* first find the slot this authority falls into... */

              const trackData: any = child.au.a[0].t;
              const ab: string[] = [
                trackData.a ? trackData.a.d.trim() : '',
                trackData.b ? trackData.b.d.trim() : ''
              ]

              let link: InterLinkAuth = null;

              am._slots.forEach((item: InterLinkAuth) => {

                if (link) {

                  /* already matched */

                  return;
                }

                /* we want to interlink that has the same before/after as the track the authority is for. */

                if (!ab.includes(item.link._before ? item.link._before._name.trim() : '')) {

                  return;
                }

                if (!ab.includes(item.link._after ? item.link._after._name.trim() : '')) {

                  return;
                }

                link = item;

              });

              if (!link) {

                Logger.error(`[AuthoritiesManager] can't find slot for authority: `, child);

                return;
              }

              /* which direction is the train moving in? */

              const train: Train = am._trainById.get(child.au.t.u);
              const track: Track = am._scenario.trackById(child.au.a[0].t.i);

              if (!train) {

                Logger.error(`[AuthoritiesManager] can't find train (${child.au.t.u}) of child: `, child);

                return;
              }

              if (!track) {

                Logger.error(`[AuthoritiesManager] can't find track (${child.au.a[0].t.i}) of child: `, child);

                return;
              }

              const dir: TrainDirection = train._tc._dir;

              /* west trains go on the left side, closer to the cursor, and east trains go on the right side */

              if (dir == TrainDirection.WEST) {

                link.west.push({train, track, reason: ''});

              } else {

                link.east.push({train, track, reason: ''});

              }

            });

            /* refresh */

            am.redraw(object, hoverObject);
          },
          (error: any) => {

            Logger.error(`[AuthoritiesManager] problems finding authorities in ${context} for '${wt}': `, error);

            // @ts-ignore
            params.fail();
          });

    } else {

      /* already have data, just redraw */

      am.redraw(object, hoverObject);
    }
  }


}
